import React from 'react';
import { useControlledState } from '@gravity-ui/uikit';
import { useDefaultTimeZone } from '../../utils/useDefaultTimeZone';
export function datePickerStateFactory({ getPlaceholderTime, mergeDateTime, setTimezone, getDateTime, useDateFieldState, }) {
    return function useDatePickerState(props) {
        var _a;
        const { disabled, readOnly } = props;
        const [isOpen, setOpen] = React.useState(false);
        const [value, setValue] = useControlledState(props.value, (_a = props.defaultValue) !== null && _a !== void 0 ? _a : null, props.onUpdate);
        const [selectedDateInner, setSelectedDate] = React.useState(null);
        const [selectedTimeInner, setSelectedTime] = React.useState(null);
        const inputTimeZone = useDefaultTimeZone(getDateTime(props.value) || getDateTime(props.defaultValue) || props.placeholderValue);
        const timeZone = props.timeZone || inputTimeZone;
        let selectedDate = selectedDateInner;
        let selectedTime = selectedTimeInner;
        const format = props.format || 'L';
        const commitValue = (date, time) => {
            if (disabled || readOnly) {
                return;
            }
            setValue(setTimezone(mergeDateTime(date, time), inputTimeZone));
            setSelectedDate(null);
            setSelectedTime(null);
        };
        const dateFieldState = useDateFieldState(Object.assign(Object.assign({}, props), { value,
            onUpdate(date) {
                if (date) {
                    commitValue(date, date);
                }
                else {
                    setValue(null);
                }
            },
            disabled,
            readOnly, validationState: props.validationState, minValue: props.minValue, maxValue: props.maxValue, isDateUnavailable: props.isDateUnavailable, format, placeholderValue: props.placeholderValue, timeZone }));
        const timeFormat = React.useMemo(() => {
            const hasSeconds = dateFieldState.sections.some((s) => s.type === 'second');
            return hasSeconds ? 'LTS' : 'LT';
        }, [dateFieldState.sections]);
        if (value) {
            selectedDate = setTimezone(value, timeZone);
            if (dateFieldState.hasTime) {
                selectedTime = setTimezone(value, timeZone);
            }
        }
        // Intercept setValue to make sure the Time section is not changed by date selection in Calendar
        const selectDate = (newValue) => {
            if (disabled || readOnly) {
                return;
            }
            const shouldClose = !dateFieldState.hasTime;
            if (dateFieldState.hasTime) {
                if (selectedTime || shouldClose) {
                    commitValue(newValue, selectedTime || newValue);
                }
                else {
                    setSelectedDate(newValue);
                }
            }
            else {
                commitValue(newValue, newValue);
            }
            if (shouldClose) {
                setOpen(false);
            }
        };
        const selectTime = (newValue) => {
            if (disabled || readOnly) {
                return;
            }
            const newTime = newValue !== null && newValue !== void 0 ? newValue : getPlaceholderTime(props.placeholderValue, timeZone);
            if (selectedDate) {
                commitValue(selectedDate, newTime);
            }
            else {
                setSelectedTime(newTime);
            }
        };
        if (dateFieldState.hasTime && !selectedTime) {
            selectedTime = dateFieldState.displayValue;
        }
        return {
            value,
            setValue(newDate) {
                if (props.readOnly || props.disabled) {
                    return;
                }
                if (newDate) {
                    setValue(setTimezone(newDate, inputTimeZone));
                }
                else {
                    setValue(null);
                }
            },
            dateValue: selectedDate,
            timeValue: selectedTime,
            setDateValue: selectDate,
            setTimeValue: selectTime,
            disabled,
            readOnly,
            format,
            hasDate: dateFieldState.hasDate,
            hasTime: dateFieldState.hasTime,
            timeFormat,
            timeZone,
            isOpen,
            setOpen(newIsOpen) {
                if (!newIsOpen && !value && selectedDate && dateFieldState.hasTime) {
                    commitValue(selectedDate, selectedTime || getPlaceholderTime(props.placeholderValue, props.timeZone));
                }
                setOpen(newIsOpen);
            },
            dateFieldState,
        };
    };
}
